/*
  CC expandable search (Test & Experiments theme).
  - Fetches products AND collections from Shopify predictive search.
  - Arrow keys move across every result (categories + products + "see all").
  - Escape closes the search panel.
  - Locks page scroll while the panel is open on mobile.
  Open/close of the panel itself is still handled by theme.js (.detail-modal.active).
*/
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.statusElement = this.querySelector('.predictive-search-status');
    this.isOpen = false;
    this.abortController = null;

    this.setupEventListeners();
    this.watchModal();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce(() => {
      this.onChange();
    }, 250).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    const clearButton = this.querySelector('[data-cc-search-clear]');
    if (clearButton) {
      clearButton.addEventListener('click', () => {
        this.close(true);
        this.input.focus();
      });
    }
  }

  // Keep a class on <html> in sync with the theme's modal state, so CSS can
  // lock scrolling and the clear button can show/hide.
  watchModal() {
    this.modal = this.closest('.detail-modal');
    if (!this.modal) return;

    const sync = () => {
      const active = this.modal.classList.contains('active');
      document.documentElement.classList.toggle('cc-search-open', active);
      // theme.js hides results on close with an inline display:none; undo it
      this.predictiveSearchResults.style.removeProperty('display');
      if (!active || !this.getQuery().length) {
        // theme.js clears the input on close, so reset our state to match
        this.close(true);
      }
    };
    new MutationObserver(sync).observe(this.modal, { attributes: true, attributeFilter: ['class'] });

    // The desktop search bar is a styled trigger; let keyboard users open it too
    const trigger = this.modal.querySelector('.modal__toggle-open');
    if (trigger) {
      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          trigger.click();
        }
      });
    }

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.modal.classList.contains('active')) {
        const closeButton = this.modal.querySelector('.search-modal__close-button');
        if (closeButton) closeButton.click();
      }
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  updateClearState() {
    this.classList.toggle('has-query', this.input.value.length > 0);
  }

  onChange() {
    this.updateClearState();
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) {
      event.preventDefault();
      return;
    }
    const selectedLink = this.querySelector('[aria-selected="true"] a');
    if (selectedLink) {
      event.preventDefault();
      selectedLink.click();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  getOptions() {
    return Array.from(this.querySelectorAll('[role="option"]'));
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const options = this.getOptions();
    if (!options.length) return;

    const selected = this.querySelector('[aria-selected="true"]');
    let index = options.indexOf(selected);

    if (direction === 'up') {
      if (index === -1) return;
      index = index <= 0 ? options.length - 1 : index - 1;
    } else {
      index = index === -1 || index >= options.length - 1 ? 0 : index + 1;
    }

    const active = options[index];
    if (selected) selected.setAttribute('aria-selected', 'false');
    active.setAttribute('aria-selected', 'true');
    active.scrollIntoView({ block: 'nearest' });

    this.setLiveRegionText(active.textContent.replace(/\s+/g, ' ').trim());
    this.input.setAttribute('aria-activedescendant', active.id);
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    const params = new URLSearchParams();
    params.set('q', searchTerm);
    params.set('resources[type]', 'product,collection');
    params.set('resources[limit]', '8');
    params.set('resources[limit_scope]', 'each');
    params.set('resources[options][unavailable_products]', 'last');
    params.set('section_id', 'predictive-search');

    fetch(`${routes.predictive_search_url}?${params.toString()}`, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('No predictive search section');
        const resultsMarkup = section.innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        // Ignore late responses for a query the shopper has already changed
        if (this.getQuery().replace(/\s+/g, '-').toLowerCase() !== queryKey) return;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.removeAttribute('loading');
        this.close();
        console.error(error);
      });
  }

  setLiveRegionLoadingState() {
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const count = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (count) this.setLiveRegionText(count.textContent.trim());
  }

  open() {
    this.predictiveSearchResults.style.removeProperty('display');
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this.updateClearState();
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', 'false');

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.removeAttribute('loading');
    this.input.setAttribute('aria-expanded', false);
    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
